import React, { FC, useRef, ChangeEvent } from 'react';
import { Box, Checkbox } from '@mui/material';
import classnames from 'classnames';
import { isEmpty } from 'lodash';
import { useBoundStore } from 'store';
import { useDocumentPrintContext } from 'store/DocumentPrint';
import { usePartialPublish, handleBlockClick, useBlocksToPublish } from 'store/DocumentPublish';
import { BlockVariant, DocumentItemType } from 'clients/documents/documentsClient.types';
import { ListItem } from 'modules/Document/List/ListItem';
import { getHeaderVariant } from 'modules/Document/Article/helpers';
import { getFieldChangeColor } from 'modules/Document/helpers';
import { useNotes } from 'shared/hooks/useNotes';
import FileButton from 'shared/components/form/FileButton/FileButton';
import { QuestionAnswerButton } from 'shared/components/form/QuestionAnswerButton/QuestionAnswerButton';
import { ContentBlock } from './ContentBlock';
import { Explanation } from './Explanation';
import { SpreadSheet } from './SpreadSheet/SpreadSheet';

export const Block: FC<any> = ({
																 parent,
																 block,
																 type,
																 titleVariant,
																 blockStyle = {},
																 listValue,
																 siblings,
																 showComments,
															 }) => {
  const ref = useRef<HTMLElement>();
  useBoundStore((state) => state.historyData);
  if (block.variant === BlockVariant.Spreadsheet) {
    return (
      <Box
        ref={ref}
        className={classnames({ 'is-full-renvoi': block.is_full_renvoi })}
      >
        <SpreadSheet
          block={block}
          style={{ marginLeft: !!type ? '-15px' : undefined, ...blockStyle }}
          parent={parent}
          siblings={siblings}
        />
      </Box>
    );
  } else if ([BlockVariant.Explanation, BlockVariant.Recommendation].includes(block.variant)) {
    const titleBg = getFieldChangeColor(block.id, 'block', 'title');
    const textBg = getFieldChangeColor(block.id, 'block', 'text_content');

    return (
      <Explanation
        ref={ref}
        className={classnames({ 'is-full-renvoi': block.is_full_renvoi })}
        style={{ marginLeft: !!type ? '-30px' : undefined, ...blockStyle }}
      >
        <ContentBlock
          parent={parent}
          titleVariant={getHeaderVariant(3)}
          block={block}
          siblings={siblings}
          isExplanationBlock
          showComments={showComments}
          titleBg={titleBg}
          textBg={textBg}
        />
      </Explanation>
    );
  }

  const titleBg = getFieldChangeColor(block.id, 'block', 'title');
  const textBg = getFieldChangeColor(block.id, 'block', 'text_content');

  return (
    <ListItem
      ref={ref}
      type={block.display_list_formatting ? type : ''}
      style={blockStyle}
      value={listValue}
      displayListFormatting={block.display_list_formatting}
      showMarker={block.title || block.text_content}
    >
      <ContentBlock
        parent={parent}
        block={block}
        titleVariant={titleVariant || block.title_variant}
        siblings={siblings}
        showComments={showComments}
        titleBg={titleBg}
        textBg={textBg}
      />
    </ListItem>
  );
};

export const BlockWrapper: FC<any> = (props) => {
  const { isPrintMode, isPrintPreview, itemsToPrint, toggleItemToPrint } = useDocumentPrintContext();
  const { groupByBlock } = useNotes();
  const partialPublish = usePartialPublish();
  const blocksToPublish = useBlocksToPublish();
  const showDraftVersion = useBoundStore((state) => state.showDraftVersion);

  const onPublishCheckboxChange = (event: ChangeEvent<HTMLInputElement>) => {
    const checked = event.target.checked;
    handleBlockClick(props.block.id, checked, true, props.block);
  };

  const isBlockSelected = blocksToPublish.includes(props.block.id);

  return (
    <Box
      position="relative"
      className={classnames({
        'is-renvoi': props.block.is_renvoi,
        'is-full-renvoi': props.block.is_full_renvoi,
        'document-child-item': props.allowPrintSelection,
      })}
      id={props.block.slug}
    >
      {(!showDraftVersion && partialPublish) && (
        <Checkbox
          checked={isBlockSelected}
          size="small"
          sx={{
            position: 'absolute',
            top: 0,
            left: '-30px',
            zIndex: 10,
            backgroundColor: 'rgba(255, 255, 255, 0.8)',
            borderRadius: '4px',
          }}
          onChange={onPublishCheckboxChange}
        />
      )}

      {(props.allowPrintSelection && isPrintMode && !isPrintPreview) && (
        <span data-slug={props.block.slug}>
          <Checkbox
            key={`${itemsToPrint[props.block.slug]}`}
            defaultChecked={itemsToPrint[props.block.slug]}
            size="small"
            className="print-selection-checkbox"
            sx={{ ml: -2, mt: -0.5 }}
            onChange={event => toggleItemToPrint(props.block.slug, event.target.checked)}
          />
        </span>
      )}

      {!props.block.title && props.showComments && (
        <FileButton
          block={props.block}
          sx={
            props.block.has_questions
              ? { position: 'absolute', top: 0, left: partialPublish && !showDraftVersion ? '-78px' : '-48px' }
              : { position: 'absolute', top: 0, right: '-20px' }
          }
        />
      )}

      <Block {...props} />

      {props.showComments && (props.block.has_questions || !isEmpty(groupByBlock[props.block.id])) && !props.block.title && (
        <QuestionAnswerButton
          item={props.block}
          itemType={DocumentItemType.Block}
          itemQuestions={props.block.questions}
          sx={{ position: 'absolute', top: 0, right: '-20px' }}
        />
      )}
    </Box>
  );
};
